import React, { useMemo, useState } from "react";
import Accordion from "@components/Accordion/Accordion";
import OverflowTooltip from "@components/OverflowTooltip/OverflowTooltip";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type Company } from "@services/vessel-call/types";
import type { Vessel } from "@services/vessel/types";
import { calculateShipAge } from "@utils/date-time";
import { capitalizeWord } from "@utils/string";
import { useTranslation } from "react-i18next";

interface VesselDetailsProps {
  testId: string;
  vesselDetails: Vessel | undefined;
}
const LANG_BASE = "vessel-nomination.add.details";

export const SingleDetail: React.FC<{ label: string; value: string | number | undefined }> = ({
  label,
  value,
}) => {
  const displayValue = value ?? "—";

  return (
    <div className="flex flex-col min-w-0">
      <dt className="text-sm text-slate-700">{label}</dt>
      <dd className="mt-0.5 min-w-0 text-sm font-bold text-slate-900">
        <OverflowTooltip
          title={displayValue}
          placement="top"
          getTarget={(wrapperEl) =>
            wrapperEl.querySelector("[data-overflow-tooltip-target='true']") as HTMLElement | null
          }
        >
          <span
            data-overflow-tooltip-target="true"
            className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {displayValue}
          </span>
        </OverflowTooltip>
      </dd>
    </div>
  );
};

const VesselDetails: React.FC<VesselDetailsProps> = ({ testId, vesselDetails }) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(true);

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const agencyName = useMemo(() => {
    return companies.find((c) => c.companyCode === user?.companyId)?.companyNameEnglish;
  }, [companies, user]);

  const flag = useMemo(() => {
    if (vesselDetails?.country?.countryCode && vesselDetails?.country?.countryName) {
      return `${vesselDetails.country.countryCode}, ${capitalizeWord(vesselDetails.country.countryName)}`;
    }
    return undefined;
  }, [vesselDetails]);

  const detailColumns = [
    [
      { label: t(`${LANG_BASE}.imo-number`), value: vesselDetails?.vesselImoNumber },
      { label: t(`${LANG_BASE}.vessel-name`), value: vesselDetails?.vesselName },
      {
        label: t(`${LANG_BASE}.type`),
        value: vesselDetails?.vesselType?.vesselTypeDesc?.toUpperCase(),
      },
    ],
    [
      { label: t(`${LANG_BASE}.flag`), value: flag },
      ...(agencyName
        ? [{ label: t(`${LANG_BASE}.agency`), value: capitalizeWord(agencyName) }]
        : []),
      {
        label: t(`${LANG_BASE}.age`),
        value: calculateShipAge(vesselDetails?.dateOfDelivery, t),
      },
    ],
    [
      { label: t(`${LANG_BASE}.loa`), value: vesselDetails?.loa },
      { label: t(`${LANG_BASE}.beam`), value: vesselDetails?.beam },
      { label: t(`${LANG_BASE}.max-draft`), value: vesselDetails?.draftMax },
    ],
    [
      // TODO: Empty during form population, will be displayed in details page
      { label: t(`${LANG_BASE}.vessel-condition`), value: undefined },
      {
        label: t(`${LANG_BASE}.gross-tonnage`),
        value: vesselDetails?.grt,
      },
    ],
  ];

  return (
    <Accordion
      testId={`${testId}-vessel-details`}
      summary={t(`${LANG_BASE}.title`)}
      expanded={isOpen}
      onChange={() => setIsOpen(!isOpen)}
      isTransparent={true}
    >
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:grid-rows-2 md:grid-flow-col xl:grid-cols-4 xl:grid-rows-1">
        {detailColumns.map((col, idx) => (
          <div key={idx} className="min-w-0">
            <div className="space-y-6">
              {col.map((item) => (
                <SingleDetail key={item.label} label={item.label} value={item.value} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Accordion>
  );
};

export default VesselDetails;
